import mapKeys from "lodash/mapKeys"
import mapValues from "lodash/mapValues"
import camelCase from "lodash/camelCase"
import isPlainObject from "lodash/isPlainObject"
import snakeCase from "lodash/snakeCase"
import ky, { HTTPError, KyResponse } from "ky"
import { environment } from "@/lib/environment"
import useStore from "@/store"
import { NavigateFunction } from "react-router"
import { toast } from "sonner"
import { posthog } from "@/lib/posthog"
import { trim } from "lodash"

let navigateFn: any | undefined

export const setNavigate = (navFn: NavigateFunction) => {
  navigateFn = navFn
}

export const navigate: NavigateFunction = (...args) => {
  if (navigateFn) {
    navigateFn(...args)
  } else {
    console.warn("navigate function not set yet")
  }
}

export function mapKeysToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(mapKeysToCamelCase)
  } else if (isPlainObject(obj)) {
    return mapValues(
      mapKeys(obj, (_value, key) => camelCase(key)),
      (value) => mapKeysToCamelCase(value)
    )
  }

  return obj
}

export function mapKeysToSnakeCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(mapKeysToSnakeCase)
  } else if (isPlainObject(obj)) {
    return mapValues(
      mapKeys(obj, (_value, key) => snakeCase(key)),
      (value) => mapKeysToSnakeCase(value)
    )
  }

  return obj
}

/**
 * Clone a response with a new body
 */
export async function cloneResponse(res: KyResponse, newBody: BodyInit) {
  return new Response(newBody, {
    status: res.status,
    statusText: res.statusText,
    headers: res.headers,
  })
}

/**
 * Base api object
 */
export const api = ky.extend({
  prefixUrl: environment.API_URL,
  timeout: environment.MODE === "development" ? false : 60000,
  hooks: {
    beforeRequest: [
      function (req) {
        const store = useStore.getState().user
        req.headers.set("Authorization", `Bearer ${store.token}`)

        return req
      },
    ],
    beforeError: [
      async (error) => {
        try {
          const { response } = error
          if (response.status === 401) {
            useStore.getState().reset()
            await navigate("/login")
          }

          if (response && response.body) {
            const body: any = await response.json()

            const message =
              body?.user_message || body?.error_message || error.message
            error.message = message
            ;(error as any).responseBody = body
          }

          return error
        } catch {
          return error
        }
      },
    ],
  },
})

async function navigateToErrorPage(navigate: NavigateFunction) {
  if (trim(window.location.pathname, "/ ") === "network-error") {
    return
  }

  await navigate("/network-error")
}

export function isBackendUnavailableError(error: unknown): boolean {
  if (!(error instanceof HTTPError)) {
    return false
  }

  const responseBody = (error as HTTPError & {
    responseBody?: { error_code?: string }
  }).responseBody

  return (
    error.response.status === 503 || responseBody?.error_code === "SYS_S02"
  )
}

export async function handleNetworkError(
  error: any,
  navigate: NavigateFunction
): Promise<boolean> {
  posthog.captureException(error)

  if (error.name === "TypeError" || isBackendUnavailableError(error)) {
    await navigateToErrorPage(navigate)

    return true
  } else if (error.name === "AbortError") {
    toast.error("API Abort Error")

    return true
  }

  return false
}

export function isNetworkUnavailableError(error: unknown): boolean {
  return (
    !navigator.onLine ||
    (error as { name?: string } | null)?.name === "TypeError" ||
    isBackendUnavailableError(error)
  )
}

export function handleOfflineActionError(
  error: unknown,
  options: {
    action: "end" | "discard"
    target?: "shift" | "process"
  }
): boolean {
  if (!isNetworkUnavailableError(error)) {
    return false
  }

  const { action, target = "shift" } = options

  if (action === "end") {
    toast.error(`Can't end ${target} without network`)
  } else {
    toast.error(
      `You need an internet connection to ${action} the ${target}. Try again after connecting to the network.`
    )
  }

  return true
}
